// ============================================================================
// ImportContactsModal.jsx  —  FICHEIRO ÚNICO (tudo incluído)
//
// Contém, no mesmo ficheiro:
//   1) Helpers reutilizados de RegisterOrUpdateContactModal (províncias, tipos,
//      validação, mapeamento, conversão Excel -> contacto, chamadas à API)
//   2) ImportDataTable — tabela genérica editável/selecionável
//   3) ImportContactsModal — o modal principal com as 11 etapas (export default)
//   4) ExportContactsButton — botão "Exportar contactos" (export nomeado)
//
// O RegisterOrUpdateContactModal.jsx original NÃO é alterado nem substituído.
// Depende de: npm install xlsx
// ============================================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Spinner,
  Alert,
  ProgressBar,
  Badge,
  ButtonGroup,
  Table,
  InputGroup,
  Pagination,
  Dropdown,
} from "react-bootstrap";
import * as XLSX from "xlsx";
import axios from "axios";
import { toast } from "react-toastify"; 
import ClickAllRefreshButtons from "../ClickAllRefreshButtons";
import Hoot from "../Hoot";

// ============================================================================
// 1) HELPERS — extraídos/replicados de RegisterOrUpdateContactModal.jsx
// ============================================================================

// importContactsHelpers.js
//
// Regras de negócio extraídas de RegisterOrUpdateContactModal.jsx para que os
// contactos importados via Excel sejam criados EXATAMENTE como um contacto
// criado manualmente (mesmos campos, mesmo endpoint, mesmo formato de envio).
//
// O ficheiro original NÃO é alterado — este módulo apenas replica/reexporta
// a mesma lógica para ser reutilizada pelo novo ImportContactsModal.

// ---------------------------------------------------------------------------
// Normalização de texto (igual ao original)
// ---------------------------------------------------------------------------
export function normalize(str) {
  return str
    ? String(str)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim()
    : "";
}

// ---------------------------------------------------------------------------
// Províncias (copiado tal e qual de RegisterOrUpdateContactModal)
// ---------------------------------------------------------------------------
export const provinciasCidades = {
  bengo: ["Bengo", "Caxito"],
  benguela: ["Benguela"],
  bie: ["Bié", "Kuito"],
  cabinda: ["Cabinda"],
  "cuando cubango": ["Cuando Cubango", "Menongue"],
  "cuanza norte": ["Cuanza Norte", "Ndalatando"],
  "cuanza sul": ["Cuanza Sul", "Sumbe"],
  cunene: ["Cunene", "Ondjiva"],
  huambo: ["Huambo"],
  huila: ["Huíla", "Lubango"],
  luanda: ["Luanda"],
  "lunda norte": ["Lunda Norte", "Dundo"],
  "lunda sul": ["Lunda Sul", "Saurimo"],
  malanje: ["Malanje"],
  moxico: ["Moxico", "Luena"],
  namibe: ["Namibe", "Mocamedes"],
  uige: ["Uíge"],
  zaire: ["Zaire", "M'Banza Congo"],
};

export function mapProvinceToSelect(provinceFromDB) {
  const normalizedDB = normalize(provinceFromDB);
  for (const key of Object.keys(provinciasCidades)) {
    const aliases = provinciasCidades[key];
    if (aliases.some((a) => normalize(a) === normalizedDB)) return key;
  }
  // aceita também o próprio key (ex.: "cuando cubango") vindo do Excel
  if (Object.prototype.hasOwnProperty.call(provinciasCidades, normalizedDB)) {
    return normalizedDB;
  }
  return "";
}

export function prettyProvinceLabel(key) {
  return provinciasCidades[key]?.[0] || key;
}

export function getProvinceOptions() {
  return Object.keys(provinciasCidades)
    .map((key) => ({ key, label: prettyProvinceLabel(key) }))
    .sort((a, b) => a.label.localeCompare(b.label, "pt"));
}

// ---------------------------------------------------------------------------
// Países (mesma lista do <Form.Select name="ed_contact_country"> original)
// ---------------------------------------------------------------------------
export const COUNTRY_OPTIONS = [
  "Angola",
  "Brasil",
  "Portugal",
  "Moçambique",
  "Cabo Verde",
  "Guiné-Bissau",
  "São Tomé e Príncipe",
  "Timor-Leste",
  "Guiné Equatorial",
  "Outros",
];

export function mapCountryToSelect(countryFromExcel) {
  const n = normalize(countryFromExcel);
  const found = COUNTRY_OPTIONS.find((c) => normalize(c) === n);
  return found || countryFromExcel || "";
}

// ---------------------------------------------------------------------------
// Tipo de contacto (0 = Colégio, 1 = Universidade — igual ao original)
// ---------------------------------------------------------------------------
export const TYPE_OPTIONS = [
  { value: "0", label: "Colégio" },
  { value: "1", label: "Universidade" },
];

export function normalizeTypeValue(raw) {
  if (raw === null || raw === undefined || raw === "") return "";
  const n = normalize(String(raw));
  if (n === "0" || n.includes("coleg") || n.includes("escola")) return "0";
  if (n === "1" || n.includes("universi") || n.includes("faculdade")) return "1";
  return "";
}

// ---------------------------------------------------------------------------
// Campos do contacto — fonte da verdade: RegisterOrUpdateContactModal
// Endpoint de criação: POST `${Hoot()}contacts/create` (multipart/form-data)
// ---------------------------------------------------------------------------
export const CONTACT_FIELDS = [
  { key: "ed_contact_name", label: "Nome", required: true, kind: "text" },
  { key: "ed_contact_phone", label: "Telefone", required: true, kind: "text" },
  { key: "ed_contact_email", label: "Email", required: true, kind: "email" },
  { key: "ed_contact_country", label: "País", required: true, kind: "select" },
  { key: "ed_contact_province", label: "Província", required: true, kind: "province" },
  { key: "ed_contact_address", label: "Endereço", required: true, kind: "text" },
  { key: "ed_contact_type", label: "Tipo", required: true, kind: "type" },
  { key: "ed_contact_comercial", label: "Comercial responsável", required: true, kind: "comercial" },
];

// Este campo existe no contrato do backend mas não é preenchível a partir do
// Excel (é um ficheiro). Mantemos a chave apenas para o FormData ficar
// idêntico ao do modal manual.
export const NON_MAPPABLE_FIELDS = ["ed_contact_picture"];

// ---------------------------------------------------------------------------
// Mapeamento automático Excel -> campos do contacto
// ---------------------------------------------------------------------------
const FIELD_KEYWORDS = {
  ed_contact_name: ["nome", "nome da escola", "escola", "instituicao", "instituição", "colegio", "universidade", "name"],
  ed_contact_phone: ["telefone", "telemovel", "telemóvel", "contacto", "phone", "numero", "número", "tlf"],
  ed_contact_email: ["email", "e-mail", "correio"],
  ed_contact_country: ["pais", "país", "country"],
  ed_contact_province: ["provincia", "província", "province"],
  ed_contact_address: ["endereco", "endereço", "morada", "address", "localizacao", "localização"],
  ed_contact_type: ["tipo", "type", "categoria"],
  ed_contact_comercial: ["comercial", "responsavel", "responsável", "vendedor", "agente"],
};

export function guessMapping(headers) {
  const mapping = {};
  headers.forEach((header) => {
    const n = normalize(header);
    let match = "skip";
    for (const [field, keywords] of Object.entries(FIELD_KEYWORDS)) {
      if (keywords.some((kw) => n === normalize(kw) || n.includes(normalize(kw)))) {
        match = field;
        break;
      }
    }
    mapping[header] = match;
  });
  return mapping;
}

// ---------------------------------------------------------------------------
// Validação
// ---------------------------------------------------------------------------
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_DIGITS_MIN = 9; // mínimo de dígitos para considerar um telefone válido

export function validatePhone(value) {
  if (!value) return false;
  const digits = String(value).replace(/\D/g, "");
  return digits.length >= PHONE_DIGITS_MIN;
}

export function validateEmail(value) {
  return EMAIL_REGEX.test(String(value || "").trim());
}

/**
 * @param {object} contact objeto já convertido (chaves ed_contact_*)
 * @param {Set<string>} comerciaisNamesNormalized nomes válidos, normalizados
 */
export function validateContact(contact, comerciaisNamesNormalized) {
  const errors = {};

  if (!contact.ed_contact_name || !String(contact.ed_contact_name).trim()) {
    errors.ed_contact_name = "Nome em falta";
  }

  if (!contact.ed_contact_phone || !String(contact.ed_contact_phone).trim()) {
    errors.ed_contact_phone = "Telefone obrigatório";
  } else if (!validatePhone(contact.ed_contact_phone)) {
    errors.ed_contact_phone = "Telefone inválido";
  }

  if (!contact.ed_contact_email || !String(contact.ed_contact_email).trim()) {
    errors.ed_contact_email = "Email obrigatório";
  } else if (!validateEmail(contact.ed_contact_email)) {
    errors.ed_contact_email = "Email inválido";
  }

  if (!contact.ed_contact_country || !String(contact.ed_contact_country).trim()) {
    errors.ed_contact_country = "País em falta";
  }

  if (!contact.ed_contact_province) {
    errors.ed_contact_province = "Província inválida";
  }

  if (!contact.ed_contact_address || !String(contact.ed_contact_address).trim()) {
    errors.ed_contact_address = "Endereço em falta";
  }

  if (contact.ed_contact_type !== "0" && contact.ed_contact_type !== "1") {
    errors.ed_contact_type = "Tipo inválido";
  }

  if (!contact.ed_contact_comercial || !String(contact.ed_contact_comercial).trim()) {
    errors.ed_contact_comercial = "Comercial não encontrado";
  } else if (comerciaisNamesNormalized && comerciaisNamesNormalized.size > 0) {
    if (!comerciaisNamesNormalized.has(normalize(contact.ed_contact_comercial))) {
      errors.ed_contact_comercial = "Comercial não encontrado";
    }
  }

  return { errors, isValid: Object.keys(errors).length === 0 };
}

// Deteta duplicados dentro do próprio lote importado (por email OU telefone)
export function findDuplicateIds(contacts) {
  const seenEmail = new Map();
  const seenPhone = new Map();
  const duplicateIds = new Set();

  contacts.forEach((c) => {
    const email = normalize(c.ed_contact_email);
    const phoneDigits = String(c.ed_contact_phone || "").replace(/\D/g, "");

    if (email) {
      if (seenEmail.has(email)) {
        duplicateIds.add(c._id);
        duplicateIds.add(seenEmail.get(email));
      } else {
        seenEmail.set(email, c._id);
      }
    }
    if (phoneDigits) {
      if (seenPhone.has(phoneDigits)) {
        duplicateIds.add(c._id);
        duplicateIds.add(seenPhone.get(phoneDigits));
      } else {
        seenPhone.set(phoneDigits, c._id);
      }
    }
  });

  return duplicateIds;
}

// ---------------------------------------------------------------------------
// Conversão linha Excel -> contacto
// ---------------------------------------------------------------------------
/**
 * @param {object} row  { headerOriginal: valorCelula }
 * @param {object} mapping { headerOriginal: campoContacto | 'skip' }
 */
export function convertRowToContact(row, mapping) {
  const contact = {
    ed_contact_name: "",
    ed_contact_phone: "",
    ed_contact_email: "",
    ed_contact_country: "",
    ed_contact_province: "",
    ed_contact_address: "",
    ed_contact_type: "",
    ed_contact_comercial: "",
    ed_contact_picture: null,
  };

  Object.entries(mapping).forEach(([header, field]) => {
    if (!field || field === "skip") return;
    const rawValue = row[header];
    if (rawValue === undefined || rawValue === null) return;
    const value = String(rawValue).trim();
    if (!value) return;

    if (field === "ed_contact_province") {
      contact.ed_contact_province = mapProvinceToSelect(value);
    } else if (field === "ed_contact_type") {
      contact.ed_contact_type = normalizeTypeValue(value);
    } else if (field === "ed_contact_country") {
      contact.ed_contact_country = mapCountryToSelect(value);
    } else {
      contact[field] = value;
    }
  });

  return contact;
}

// Constrói o FormData exatamente como RegisterOrUpdateContactModal.handleSubmit
export function buildContactFormData(contact) {
  const formData = new FormData();
  Object.entries(contact).forEach(([key, value]) => {
    if (value !== null && value !== undefined) formData.append(key, value);
  });
  return formData;
}

// ---------------------------------------------------------------------------
// Comerciais — mesma chamada/filtragem usada em RegisterOrUpdateContactModal
// ---------------------------------------------------------------------------
function asArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

export async function fetchComerciais(Hoot) {
  const res = await axios.get(`${Hoot()}comerciais`, {
    withCredentials: true,
    params: { page: 1, limit: 200, status: "ativo" },
  });
  const data = asArray(res.data);
  const onlyActive = data.filter((c) => String(c?.status || "").toLowerCase() !== "inativo");
  onlyActive.sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), "pt"));
  return onlyActive;
}

// Envia um único contacto usando exatamente o mesmo endpoint/método/formato
// do modal manual (POST `${Hoot()}contacts/create`).
export async function submitContact(Hoot, contact) {
  const formData = buildContactFormData(contact);
  return axios.post(`${Hoot()}contacts/create`, formData, {
    withCredentials: true,
    headers: { "Content-Type": "multipart/form-data" },
  });
}

// ============================================================================
// 2) ImportDataTable — tabela genérica editável/selecionável
// ============================================================================

const PAGE_SIZE = 25;

/**
 * Tabela genérica, editável e selecionável, usada para:
 *  - Etapa 3: editar os dados brutos do Excel (headers + células)
 *  - Etapa 6: rever os contactos já convertidos, antes do envio
 *
 * Props:
 *  columns: [{ key, label, editableHeader?: bool, removable?: bool, width?: string }]
 *  rows:    [{ id, selected, values: { [colKey]: value }, errors?: { [colKey]: msg } }]
 *  onCellChange(rowId, colKey, newValue)
 *  onHeaderChange(colKey, newLabel)
 *  onToggleRow(id)
 *  onToggleAll(checked)
 *  onRemoveRow(id)
 *  onRemoveSelected()
 *  onRemoveColumn(colKey)
 *  searchTerm, onSearchChange(term)
 *  emptyMessage
 */
function ImportDataTable({
  columns,
  rows,
  onCellChange,
  onHeaderChange,
  onToggleRow,
  onToggleAll,
  onRemoveRow,
  onRemoveSelected,
  onRemoveColumn,
  searchTerm = "",
  onSearchChange,
  emptyMessage = "Sem dados para mostrar.",
}) {
  const [page, setPage] = useState(1);

  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter((row) =>
      Object.values(row.values).some((v) => String(v ?? "").toLowerCase().includes(term))
    );
  }, [rows, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filteredRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const selectedCount = rows.filter((r) => r.selected).length;
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  const someSelected = selectedCount > 0 && !allSelected;

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <InputGroup style={{ maxWidth: 320 }}>
          <InputGroup.Text>
            <i className="bi bi-search" />
          </InputGroup.Text>
          <Form.Control
            placeholder="Pesquisar..."
            value={searchTerm}
            onChange={(e) => {
              onSearchChange?.(e.target.value);
              setPage(1);
            }}
          />
        </InputGroup>

        <div className="d-flex align-items-center gap-2">
          <Badge bg="secondary">{filteredRows.length} linha(s)</Badge>
          {selectedCount > 0 && (
            <>
              <Badge bg="primary">{selectedCount} selecionada(s)</Badge>
              <Button size="sm" variant="outline-danger" onClick={onRemoveSelected}>
                Remover selecionadas
              </Button>
            </>
          )}
        </div>
      </div>

      <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 12 }}>
        <Table size="sm" bordered hover responsive className="mb-0" style={{ minWidth: 600 }}>
          <thead>
            <tr>
              <th style={{ width: 36, textAlign: "center" }}>
                <Form.Check
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => el && (el.indeterminate = someSelected)}
                  onChange={(e) => onToggleAll?.(e.target.checked)}
                />
              </th>
              {columns.map((col) => (
                <th key={col.key} style={{ minWidth: col.width || 140 }}>
                  <div className="d-flex align-items-center gap-1">
                    {col.editableHeader ? (
                      <Form.Control
                        size="sm"
                        value={col.label}
                        onChange={(e) => onHeaderChange?.(col.key, e.target.value)}
                        style={{ fontWeight: 600 }}
                      />
                    ) : (
                      <span style={{ fontWeight: 600 }}>{col.label}</span>
                    )}
                    {col.removable && (
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0 text-danger"
                        title="Remover coluna"
                        onClick={() => onRemoveColumn?.(col.key)}
                      >
                        <i className="bi bi-x-circle" />
                      </Button>
                    )}
                  </div>
                </th>
              ))}
              {onRemoveRow && <th style={{ width: 40 }} />}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 2} className="text-center text-muted py-3">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              pageRows.map((row) => (
                <tr key={row.id} className={row.errors && Object.keys(row.errors).length ? "table-danger" : ""}>
                  <td style={{ textAlign: "center" }}>
                    <Form.Check
                      type="checkbox"
                      checked={!!row.selected}
                      onChange={() => onToggleRow?.(row.id)}
                    />
                  </td>
                  {columns.map((col) => {
                    const errMsg = row.errors?.[col.key];
                    return (
                      <td key={col.key} style={{ padding: 4 }}>
                        <Form.Control
                          size="sm"
                          value={row.values[col.key] ?? ""}
                          onChange={(e) => onCellChange?.(row.id, col.key, e.target.value)}
                          isInvalid={!!errMsg}
                          style={{ border: errMsg ? undefined : "1px solid transparent", background: "transparent" }}
                        />
                        {errMsg && (
                          <div className="text-danger" style={{ fontSize: ".7rem", marginTop: 2 }}>
                            {errMsg}
                          </div>
                        )}
                      </td>
                    );
                  })}
                  {onRemoveRow && (
                    <td style={{ textAlign: "center" }}>
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0 text-danger"
                        title="Remover linha"
                        onClick={() => onRemoveRow(row.id)}
                      >
                        <i className="bi bi-trash" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-2">
          <Pagination size="sm" className="mb-0">
            <Pagination.Prev disabled={currentPage === 1} onClick={() => setPage((p) => p - 1)} />
            <Pagination.Item disabled>{`${currentPage} / ${totalPages}`}</Pagination.Item>
            <Pagination.Next disabled={currentPage === totalPages} onClick={() => setPage((p) => p + 1)} />
          </Pagination>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// 3) ImportContactsModal — modal principal (11 etapas)
// ============================================================================

// ---------------------------------------------------------------------------
// Passos do fluxo
// ---------------------------------------------------------------------------
const STEP = {
  UPLOAD: "upload",
  SHEETS: "sheets",
  EDIT: "edit",
  MAPPING: "mapping",
  REVIEW: "review",
  CONFIRM: "confirm",
  SENDING: "sending",
  RESULT: "result",
};

const STEP_ORDER = [
  STEP.UPLOAD,
  STEP.SHEETS,
  STEP.EDIT,
  STEP.MAPPING,
  STEP.REVIEW,
  STEP.CONFIRM,
  STEP.SENDING,
  STEP.RESULT,
];

const STEP_LABELS = {
  [STEP.UPLOAD]: "1. Excel",
  [STEP.SHEETS]: "2. Folhas",
  [STEP.EDIT]: "3. Editar dados",
  [STEP.MAPPING]: "4. Mapear campos",
  [STEP.REVIEW]: "5/6. Rever contactos",
  [STEP.CONFIRM]: "7. Confirmar",
  [STEP.SENDING]: "8/9. A enviar",
  [STEP.RESULT]: "10. Resultado",
};

let rowIdCounter = 0;
function nextRowId() {
  rowIdCounter += 1;
  return `row_${rowIdCounter}_${Date.now()}`;
}

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

const styles = `
.import-modal .modal-dialog{ max-width: 960px; }
@media (max-width: 992px){ .import-modal .modal-dialog{ max-width: 96vw; } }
.import-modal .modal-content{ border-radius: 18px; overflow: hidden; }
.import-modal .modal-header{ background: #ffffff; border-bottom: 1px solid #e2e8f0; }
.import-modal .modal-body{ background: linear-gradient(180deg, rgba(248,250,252,.6), #fff 30%); max-height: 72vh; overflow-y: auto; }
.import-steps{ display:flex; gap:6px; flex-wrap:wrap; margin-bottom: 16px; }
.import-step-pill{
  font-size:.72rem; font-weight:600; padding:5px 10px; border-radius:999px;
  background:#f1f5f9; color:#64748b; border:1px solid #e2e8f0;
}
.import-step-pill.active{ background:#5b5cff; color:#fff; border-color:#5b5cff; }
.import-step-pill.done{ background:#ecfdf5; color:#059669; border-color:#a7f3d0; }
.dropzone{
  border: 2px dashed #cbd5e1; border-radius: 16px; padding: 40px 20px;
  text-align:center; background:#fff; cursor:pointer; transition:.15s;
}
.dropzone:hover{ border-color:#5b5cff; background: rgba(91,92,255,.03); }
.sheet-row{
  display:flex; align-items:center; justify-content:space-between;
  padding:10px 12px; border:1px solid #e2e8f0; border-radius:12px; margin-bottom:8px; background:#fff;
}
.mapping-row{
  display:grid; grid-template-columns: 1fr 32px 1fr; align-items:center; gap:10px;
  padding:8px 10px; border:1px solid #e2e8f0; border-radius:12px; margin-bottom:8px; background:#fff;
}
.summary-grid{ display:grid; grid-template-columns: repeat(auto-fit, minmax(140px,1fr)); gap:10px; margin: 14px 0; }
.summary-card{ background:#fff; border:1px solid #e2e8f0; border-radius:14px; padding:12px 14px; text-align:center; }
.summary-card .value{ font-size:1.4rem; font-weight:700; color:#0f172a; }
.summary-card .label{ font-size:.72rem; color:#64748b; font-weight:600; }
.error-row{ display:flex; justify-content:space-between; border-bottom:1px solid #f1f5f9; padding:8px 0; }
`;

export default function ImportContactsModal({ toggle_btn }) {
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(STEP.UPLOAD);

  // ---- Etapa 1: ficheiro -------------------------------------------------
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  const workbookRef = useRef(null);

  // ---- Etapa 2: folhas -----------------------------------------------------
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheets, setSelectedSheets] = useState(new Set());

  // ---- Etapa 3: tabela editável ---------------------------------------------
  const [columns, setColumns] = useState([]); // [{key,label,editableHeader:true,removable:true}]
  const [rawRows, setRawRows] = useState([]); // [{id, selected, values:{colKey:value}}]
  const [editSearch, setEditSearch] = useState("");

  // ---- Etapa 4: mapeamento --------------------------------------------------
  const [mapping, setMapping] = useState({}); // { colKey: fieldKey | 'skip' }

  // ---- Etapa 5/6: contactos convertidos + revisão ----------------------------
  const [contacts, setContacts] = useState([]); // [{id, selected, values:{ed_contact_*}, errors}]
  const [reviewSearch, setReviewSearch] = useState("");
  const [reviewFilter, setReviewFilter] = useState("all"); // all | valid | errors

  // ---- Comerciais -------------------------------------------------------
  const [comerciais, setComerciais] = useState([]);
  const [comerciaisLoading, setComerciaisLoading] = useState(false);
  const [comerciaisError, setComerciaisError] = useState(null);

  // ---- Envio --------------------------------------------------------------
  const [sending, setSending] = useState(false);
  const [sendStats, setSendStats] = useState({ total: 0, processed: 0, success: 0, errors: 0 });
  const [sendResults, setSendResults] = useState([]); // [{id,name,status:'sucesso'|'erro',message}]
  const cancelRef = useRef(false);

  // -------------------------------------------------------------------------
  const openModal = () => setShow(true);

  const resetAll = () => {
    setStep(STEP.UPLOAD);
    setFile(null);
    setParsing(false);
    setParseError(null);
    workbookRef.current = null;
    setSheetNames([]);
    setSelectedSheets(new Set());
    setColumns([]);
    setRawRows([]);
    setEditSearch("");
    setMapping({});
    setContacts([]);
    setReviewSearch("");
    setReviewFilter("all");
    setSending(false);
    setSendStats({ total: 0, processed: 0, success: 0, errors: 0 });
    setSendResults([]);
    cancelRef.current = false;
  };

  const closeModal = () => {
    if (sending) return; // não fechar a meio do envio
    setShow(false);
    resetAll();
  };

  useEffect(() => {
    if (show) loadComerciais();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const loadComerciais = async () => {
    try {
      setComerciaisLoading(true);
      setComerciaisError(null);
      const list = await fetchComerciais(Hoot);
      setComerciais(list);
    } catch (e) {
      setComerciais([]);
      setComerciaisError("Erro ao carregar comerciais.");
    } finally {
      setComerciaisLoading(false);
    }
  };

  const comerciaisNamesNormalized = useMemo(
    () => new Set(comerciais.map((c) => normalize(c.name))),
    [comerciais]
  );

  // ===========================================================================
  // ETAPA 1 — Selecionar Excel
  // ===========================================================================
  const handleFileSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    const isValidExt = /\.(xlsx|xls)$/i.test(f.name);
    if (!isValidExt) {
      setParseError("Formato inválido. Selecione um ficheiro .xlsx ou .xls.");
      return;
    }

    setFile(f);
    setParseError(null);
    setParsing(true);

    try {
      const buffer = await f.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      workbookRef.current = workbook;
      const names = workbook.SheetNames || [];
      setSheetNames(names);
      setSelectedSheets(new Set(names)); // por omissão, todas selecionadas
      setStep(STEP.SHEETS);
    } catch (err) {
      setParseError("Não foi possível ler este ficheiro Excel.");
    } finally {
      setParsing(false);
    }
  };

  // ===========================================================================
  // ETAPA 2 — Selecionar folhas
  // ===========================================================================
  const toggleSheet = (name) => {
    setSelectedSheets((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleContinueSheets = () => {
    const workbook = workbookRef.current;
    if (!workbook || selectedSheets.size === 0) return;

    // Junta os dados de todas as folhas selecionadas numa única tabela.
    // Os headers são a união das primeiras linhas de cada folha.
    const headerSet = [];
    const headerSeen = new Set();
    const allRows = [];

    Array.from(selectedSheets).forEach((sheetName) => {
      const ws = workbook.Sheets[sheetName];
      if (!ws) return;
      const json = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
      json.forEach((obj) => {
        Object.keys(obj).forEach((h) => {
          if (!headerSeen.has(h)) {
            headerSeen.add(h);
            headerSet.push(h);
          }
        });
        allRows.push(obj);
      });
    });

    const cols = headerSet.map((h) => ({
      key: h,
      label: h,
      editableHeader: true,
      removable: true,
    }));

    const rows = allRows.map((obj) => {
      const values = {};
      headerSet.forEach((h) => {
        values[h] = obj[h] !== undefined ? String(obj[h]) : "";
      });
      return { id: nextRowId(), selected: true, values };
    });

    setColumns(cols);
    setRawRows(rows);
    setStep(STEP.EDIT);
  };

  // ===========================================================================
  // ETAPA 3 — Editar dados do Excel
  // ===========================================================================
  const handleCellChange = (rowId, colKey, value) => {
    setRawRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, values: { ...r.values, [colKey]: value } } : r))
    );
  };

  const handleHeaderChange = (colKey, newLabel) => {
    setColumns((prev) => prev.map((c) => (c.key === colKey ? { ...c, label: newLabel } : c)));
  };

  const handleToggleRow = (rowId) => {
    setRawRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, selected: !r.selected } : r)));
  };

  const handleToggleAllRows = (checked) => {
    setRawRows((prev) => prev.map((r) => ({ ...r, selected: checked })));
  };

  const handleRemoveRow = (rowId) => {
    setRawRows((prev) => prev.filter((r) => r.id !== rowId));
  };

  const handleRemoveSelectedRows = () => {
    setRawRows((prev) => prev.filter((r) => !r.selected));
  };

  const handleRemoveColumn = (colKey) => {
    setColumns((prev) => prev.filter((c) => c.key !== colKey));
    setRawRows((prev) =>
      prev.map((r) => {
        const values = { ...r.values };
        delete values[colKey];
        return { ...r, values };
      })
    );
  };

  const goToMapping = () => {
    setMapping(guessMapping(columns.map((c) => c.key)));
    setStep(STEP.MAPPING);
  };

  // ===========================================================================
  // ETAPA 4 — Mapear Excel -> contacto
  // ===========================================================================
  const handleMappingChange = (colKey, fieldKey) => {
    setMapping((prev) => ({ ...prev, [colKey]: fieldKey }));
  };

  // ===========================================================================
  // ETAPA 5 — Conversão e validação (executada ao entrar na revisão)
  // ===========================================================================
  const buildConvertedContacts = () => {
    // usa apenas as linhas ainda selecionadas (não removidas na etapa 3)
    const sourceRows = rawRows.filter((r) => r.selected !== false);

    // header original (columns[].key) -> label atual (permite renomear sem tocar no ficheiro original)
    const headerLabelByKey = {};
    columns.forEach((c) => (headerLabelByKey[c.key] = c.key)); // usamos sempre a key original como chave da linha

    const converted = sourceRows.map((row) => {
      const contact = convertRowToContact(row.values, mapping);
      return {
        id: row.id,
        selected: true,
        values: contact,
        errors: {},
      };
    });

    const withValidation = converted.map((c) => {
      const { errors } = validateContact(c.values, comerciaisNamesNormalized);
      return { ...c, errors };
    });

    const duplicateIds = findDuplicateIds(
      withValidation.map((c) => ({ _id: c.id, ...c.values }))
    );

    const withDuplicates = withValidation.map((c) => {
      if (duplicateIds.has(c.id)) {
        return { ...c, errors: { ...c.errors, _duplicate: "Contacto duplicado no ficheiro" } };
      }
      return c;
    });

    setContacts(withDuplicates);
  };

  const goToReview = () => {
    buildConvertedContacts();
    setStep(STEP.REVIEW);
  };

  // ===========================================================================
  // ETAPA 6 — Editar/rever contactos antes do envio
  // ===========================================================================
  const revalidateContact = (contact) => {
    const { errors } = validateContact(contact.values, comerciaisNamesNormalized);
    return { ...contact, errors };
  };

  const handleContactFieldChange = (contactId, field, value) => {
    setContacts((prev) =>
      prev.map((c) => {
        if (c.id !== contactId) return c;
        const updated = { ...c, values: { ...c.values, [field]: value } };
        return revalidateContact(updated);
      })
    );
  };

  const handleToggleContact = (contactId) => {
    setContacts((prev) => prev.map((c) => (c.id === contactId ? { ...c, selected: !c.selected } : c)));
  };

  const handleToggleAllContacts = (checked) => {
    setContacts((prev) => prev.map((c) => ({ ...c, selected: checked })));
  };

  const handleRemoveContact = (contactId) => {
    setContacts((prev) => prev.filter((c) => c.id !== contactId));
  };

  const handleRemoveSelectedContacts = () => {
    setContacts((prev) => prev.filter((c) => !c.selected));
  };

  const visibleContacts = useMemo(() => {
    let list = contacts;
    if (reviewFilter === "valid") list = list.filter((c) => Object.keys(c.errors).length === 0);
    if (reviewFilter === "errors") list = list.filter((c) => Object.keys(c.errors).length > 0);
    return list;
  }, [contacts, reviewFilter]);

  const reviewTableColumns = useMemo(
    () => CONTACT_FIELDS.map((f) => ({ key: f.key, label: f.label, editableHeader: false })),
    []
  );

  const reviewTableRows = useMemo(
    () =>
      visibleContacts.map((c) => ({
        id: c.id,
        selected: c.selected,
        values: c.values,
        errors: c.errors,
      })),
    [visibleContacts]
  );

  // ===========================================================================
  // ETAPA 7 — Resumo / confirmação
  // ===========================================================================
  const summary = useMemo(() => {
    const found = rawRows.length;
    const removed = found - rawRows.filter((r) => r.selected !== false).length;
    const total = contacts.length;
    const valid = contacts.filter((c) => Object.keys(c.errors).length === 0).length;
    const withErrors = total - valid;
    const selectedForSend = contacts.filter(
      (c) => c.selected && Object.keys(c.errors).length === 0
    ).length;
    return { found, removed, total, valid, withErrors, selectedForSend };
  }, [rawRows, contacts]);

  const goToConfirm = () => setStep(STEP.CONFIRM);

  // ===========================================================================
  // ETAPA 8/9 — Envio + progresso em tempo real
  // ===========================================================================
  const startImport = async () => {
    const toSend = contacts.filter((c) => c.selected && Object.keys(c.errors).length === 0);
    if (toSend.length === 0) {
      toast.warning("Não há contactos válidos selecionados para importar.");
      return;
    }

    cancelRef.current = false;
    setSending(true);
    setStep(STEP.SENDING);
    setSendResults([]);
    setSendStats({ total: toSend.length, processed: 0, success: 0, errors: 0 });

    const results = [];
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < toSend.length; i += 1) {
      if (cancelRef.current) break;
      const contact = toSend[i];

      try {
        await submitContact(Hoot, contact.values);
        successCount += 1;
        results.push({ id: contact.id, name: contact.values.ed_contact_name, status: "sucesso" });
      } catch (err) {
        errorCount += 1;
        const message =
          err?.response?.data?.message || err?.response?.data?.error || "Erro ao criar contacto.";
        results.push({ id: contact.id, name: contact.values.ed_contact_name, status: "erro", message });
      }

      setSendStats({ total: toSend.length, processed: i + 1, success: successCount, errors: errorCount });
      setSendResults([...results]);
    }

    setSending(false);
    setStep(STEP.RESULT);
    ClickAllRefreshButtons();
  };

  // ===========================================================================
  // ETAPA 10 — Resultado / exportar erros
  // ===========================================================================
  const exportErrorsToExcel = () => {
    const failed = sendResults.filter((r) => r.status === "erro");
    if (failed.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(
      failed.map((r) => ({ Nome: r.name || "(sem nome)", Erro: r.message }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Erros");
    XLSX.writeFile(wb, "erros_importacao_contactos.xlsx");
  };

  // ===========================================================================
  // Render
  // ===========================================================================
  const stepIndex = STEP_ORDER.indexOf(step);

  return (
    <>
      <style>{styles}</style>

      <div className="col-lg-12" onClick={openModal} style={{ display: "inline-block" }}>
        {toggle_btn}
      </div>

      <Modal show={show} onHide={closeModal} centered backdrop="static" keyboard={!sending} className="import-modal">
        <Modal.Header closeButton={!sending}>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 650 }}>Importar contactos via Excel</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="import-steps">
            {STEP_ORDER.map((s, idx) => (
              <span
                key={s}
                className={`import-step-pill ${s === step ? "active" : idx < stepIndex ? "done" : ""}`}
              >
                {STEP_LABELS[s]}
              </span>
            ))}
          </div>

          {step === STEP.UPLOAD && (
            <UploadStep file={file} parsing={parsing} parseError={parseError} onFileSelect={handleFileSelect} />
          )}

          {step === STEP.SHEETS && (
            <SheetsStep
              file={file}
              sheetNames={sheetNames}
              selectedSheets={selectedSheets}
              onToggleSheet={toggleSheet}
              workbookRef={workbookRef}
            />
          )}

          {step === STEP.EDIT && (
            <div>
              <p className="text-muted" style={{ fontSize: ".85rem" }}>
                Edite os cabeçalhos e células, remova linhas ou colunas indesejadas. Estas alterações não
                afetam o ficheiro Excel original.
              </p>
              <ImportDataTable
                columns={columns}
                rows={rawRows}
                onCellChange={handleCellChange}
                onHeaderChange={handleHeaderChange}
                onToggleRow={handleToggleRow}
                onToggleAll={handleToggleAllRows}
                onRemoveRow={handleRemoveRow}
                onRemoveSelected={handleRemoveSelectedRows}
                onRemoveColumn={handleRemoveColumn}
                searchTerm={editSearch}
                onSearchChange={setEditSearch}
                emptyMessage="Sem linhas. Volte atrás e selecione outra folha."
              />
            </div>
          )}

          {step === STEP.MAPPING && (
            <MappingStep columns={columns} mapping={mapping} onMappingChange={handleMappingChange} />
          )}

          {step === STEP.REVIEW && (
            <div>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                <p className="text-muted mb-0" style={{ fontSize: ".85rem" }}>
                  Só os contactos <strong>selecionados</strong> e <strong>sem erros</strong> serão enviados.
                </p>
                <ButtonGroup size="sm">
                  <Button
                    variant={reviewFilter === "all" ? "primary" : "outline-secondary"}
                    onClick={() => setReviewFilter("all")}
                  >
                    Todos ({contacts.length})
                  </Button>
                  <Button
                    variant={reviewFilter === "valid" ? "primary" : "outline-secondary"}
                    onClick={() => setReviewFilter("valid")}
                  >
                    Válidos ({summary.valid})
                  </Button>
                  <Button
                    variant={reviewFilter === "errors" ? "primary" : "outline-secondary"}
                    onClick={() => setReviewFilter("errors")}
                  >
                    Com erro ({summary.withErrors})
                  </Button>
                </ButtonGroup>
              </div>

              {comerciaisError && (
                <Alert variant="warning" style={{ fontSize: ".8rem" }}>
                  {comerciaisError}{" "}
                  <span style={{ textDecoration: "underline", cursor: "pointer" }} onClick={loadComerciais}>
                    Tentar novamente
                  </span>
                </Alert>
              )}

              <ImportDataTable
                columns={reviewTableColumns}
                rows={reviewTableRows}
                onCellChange={handleContactFieldChange}
                onToggleRow={handleToggleContact}
                onToggleAll={handleToggleAllContacts}
                onRemoveRow={handleRemoveContact}
                onRemoveSelected={handleRemoveSelectedContacts}
                searchTerm={reviewSearch}
                onSearchChange={setReviewSearch}
                emptyMessage="Nenhum contacto corresponde ao filtro."
              />
            </div>
          )}

          {step === STEP.CONFIRM && <ConfirmStep summary={summary} />}

          {step === STEP.SENDING && <SendingStep stats={sendStats} results={sendResults} />}

          {step === STEP.RESULT && (
            <ResultStep stats={sendStats} results={sendResults} onExportErrors={exportErrorsToExcel} />
          )}
        </Modal.Body>

        <Modal.Footer>
          <ModalFooterActions
            step={step}
            sending={sending}
            file={file}
            selectedSheets={selectedSheets}
            columns={columns}
            summary={summary}
            comerciaisLoading={comerciaisLoading}
            onClose={closeModal}
            onBack={() => setStep(STEP_ORDER[Math.max(0, stepIndex - 1)])}
            onContinueSheets={handleContinueSheets}
            onContinueEdit={goToMapping}
            onContinueMapping={goToReview}
            onContinueReview={goToConfirm}
            onStartImport={startImport}
            onFinish={closeModal}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
}

// =============================================================================
// Etapa 1 — Upload
// =============================================================================
function UploadStep({ file, parsing, parseError, onFileSelect }) {
  const inputRef = useRef(null);
  return (
    <div>
      {parseError && <Alert variant="danger">{parseError}</Alert>}

      <div className="dropzone" onClick={() => inputRef.current?.click()}>
        <i className="bi bi-file-earmark-spreadsheet" style={{ fontSize: 32, color: "#5b5cff" }} />
        <div style={{ fontWeight: 600, marginTop: 8 }}>
          {file ? file.name : "Clique para selecionar um ficheiro Excel"}
        </div>
        <div className="text-muted" style={{ fontSize: ".8rem", marginTop: 4 }}>
          Formatos aceites: .xlsx, .xls
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={onFileSelect}
        />
      </div>

      {parsing && (
        <div className="d-flex align-items-center gap-2 mt-3">
          <Spinner size="sm" animation="border" />
          <span className="text-muted" style={{ fontSize: ".85rem" }}>A ler o ficheiro...</span>
        </div>
      )}

      {file && !parsing && (
        <div className="mt-3" style={{ fontSize: ".85rem" }}>
          <div>
            <strong>Nome:</strong> {file.name}
          </div>
          <div>
            <strong>Tamanho:</strong> {formatBytes(file.size)}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Etapa 2 — Selecionar folhas
// =============================================================================
function SheetsStep({ sheetNames, selectedSheets, onToggleSheet, workbookRef }) {
  const rowCountFor = (name) => {
    const ws = workbookRef.current?.Sheets?.[name];
    if (!ws) return 0;
    const range = XLSX.utils.decode_range(ws["!ref"] || "A1:A1");
    return Math.max(0, range.e.r - range.s.r);
  };

  return (
    <div>
      <p className="text-muted" style={{ fontSize: ".85rem" }}>
        {sheetNames.length} folha(s) encontrada(s). Selecione quais pretende importar.
      </p>
      {sheetNames.map((name) => (
        <div className="sheet-row" key={name}>
          <Form.Check
            type="checkbox"
            id={`sheet-${name}`}
            label={name}
            checked={selectedSheets.has(name)}
            onChange={() => onToggleSheet(name)}
          />
          <Badge bg="light" text="dark">{rowCountFor(name)} linha(s)</Badge>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Etapa 4 — Mapeamento
// =============================================================================
function MappingStep({ columns, mapping, onMappingChange }) {
  return (
    <div>
      <p className="text-muted" style={{ fontSize: ".85rem" }}>
        Associe cada coluna do Excel a um campo do contacto. O mapeamento automático pode ser ajustado.
      </p>

      <div className="mapping-row" style={{ background: "transparent", border: "none", fontWeight: 600, fontSize: ".78rem", color: "#64748b" }}>
        <div>Coluna do Excel</div>
        <div />
        <div>Campo do contacto</div>
      </div>

      {columns.map((col) => (
        <div className="mapping-row" key={col.key}>
          <div style={{ fontWeight: 600, fontSize: ".85rem" }}>{col.label}</div>
          <i className="bi bi-arrow-right text-muted" />
          <Form.Select
            size="sm"
            value={mapping[col.key] || "skip"}
            onChange={(e) => onMappingChange(col.key, e.target.value)}
          >
            <option value="skip">Não importar esta coluna</option>
            {CONTACT_FIELDS.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
                {f.required ? " *" : ""}
              </option>
            ))}
          </Form.Select>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Etapa 7 — Confirmação
// =============================================================================
function ConfirmStep({ summary }) {
  return (
    <div>
      <h6 style={{ fontWeight: 650 }}>Resumo da importação</h6>
      <div className="summary-grid">
        <div className="summary-card">
          <div className="value">{summary.found}</div>
          <div className="label">Contactos encontrados</div>
        </div>
        <div className="summary-card">
          <div className="value">{summary.removed}</div>
          <div className="label">Linhas removidas</div>
        </div>
        <div className="summary-card">
          <div className="value" style={{ color: "#059669" }}>{summary.valid}</div>
          <div className="label">Contactos válidos</div>
        </div>
        <div className="summary-card">
          <div className="value" style={{ color: "#ef4444" }}>{summary.withErrors}</div>
          <div className="label">Contactos com erros</div>
        </div>
        <div className="summary-card">
          <div className="value" style={{ color: "#5b5cff" }}>{summary.selectedForSend}</div>
          <div className="label">Selecionados para envio</div>
        </div>
      </div>
      {summary.selectedForSend === 0 && (
        <Alert variant="warning">
          Nenhum contacto válido está selecionado. Volte à revisão para corrigir ou selecionar contactos.
        </Alert>
      )}
    </div>
  );
}

// =============================================================================
// Etapa 8/9 — Envio + progresso
// =============================================================================
function SendingStep({ stats, results }) {
  const pct = stats.total ? Math.round((stats.processed / stats.total) * 100) : 0;
  const pending = stats.total - stats.processed;

  return (
    <div>
      <h6 style={{ fontWeight: 650 }}>Importação em progresso...</h6>
      <ProgressBar now={pct} label={`${pct}%`} animated className="mb-3" style={{ height: 14, borderRadius: 999 }} />

      <div className="summary-grid">
        <div className="summary-card">
          <div className="value">{stats.total}</div>
          <div className="label">Total</div>
        </div>
        <div className="summary-card">
          <div className="value">{stats.processed}</div>
          <div className="label">Processados</div>
        </div>
        <div className="summary-card">
          <div className="value" style={{ color: "#059669" }}>{stats.success}</div>
          <div className="label">Sucesso</div>
        </div>
        <div className="summary-card">
          <div className="value" style={{ color: "#ef4444" }}>{stats.errors}</div>
          <div className="label">Erros</div>
        </div>
        <div className="summary-card">
          <div className="value">{pending}</div>
          <div className="label">Pendentes</div>
        </div>
      </div>

      <div className="text-muted" style={{ fontSize: ".8rem" }}>
        Não feche esta janela enquanto a importação estiver em curso.
      </div>

      {results.length > 0 && (
        <div className="mt-3" style={{ maxHeight: 180, overflowY: "auto" }}>
          {results.slice(-8).reverse().map((r, idx) => (
            <div className="error-row" key={idx}>
              <span>{r.name || "(sem nome)"}</span>
              <Badge bg={r.status === "sucesso" ? "success" : "danger"}>{r.status}</Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Etapa 10 — Resultado
// =============================================================================
function ResultStep({ stats, results, onExportErrors }) {
  const failed = results.filter((r) => r.status === "erro");

  return (
    <div>
      <Alert variant={stats.errors > 0 ? "warning" : "success"}>
        <strong>Importação concluída!</strong>
      </Alert>

      <div className="summary-grid">
        <div className="summary-card">
          <div className="value">{stats.total}</div>
          <div className="label">Total</div>
        </div>
        <div className="summary-card">
          <div className="value" style={{ color: "#059669" }}>{stats.success}</div>
          <div className="label">Enviados</div>
        </div>
        <div className="summary-card">
          <div className="value" style={{ color: "#ef4444" }}>{stats.errors}</div>
          <div className="label">Falharam</div>
        </div>
      </div>

      {failed.length > 0 && (
        <div className="mt-3">
          <div className="d-flex align-items-center justify-content-between">
            <h6 style={{ fontWeight: 650 }}>Contactos com erro</h6>
            <Button size="sm" variant="outline-secondary" onClick={onExportErrors}>
              <i className="bi bi-download me-1" />
              Exportar erros para Excel
            </Button>
          </div>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {failed.map((r, idx) => (
              <div className="error-row" key={idx}>
                <span>{r.name || "(sem nome)"}</span>
                <span className="text-danger" style={{ fontSize: ".8rem" }}>{r.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Rodapé — ações por etapa
// =============================================================================
function ModalFooterActions({
  step,
  sending,
  file,
  selectedSheets,
  columns,
  summary,
  comerciaisLoading,
  onClose,
  onBack,
  onContinueSheets,
  onContinueEdit,
  onContinueMapping,
  onContinueReview,
  onStartImport,
  onFinish,
}) {
  if (step === STEP.RESULT) {
    return (
      <Button variant="primary" onClick={onFinish}>
        Concluir
      </Button>
    );
  }

  if (step === STEP.SENDING) {
    return (
      <Button variant="outline-secondary" disabled>
        A importar, aguarde...
      </Button>
    );
  }

  return (
    <div className="d-flex justify-content-between w-100">
      <div>
        {step !== STEP.UPLOAD && (
          <Button variant="outline-secondary" className="btn-soft" onClick={onBack}>
            Voltar
          </Button>
        )}
      </div>
      <div className="d-flex gap-2">
        <Button variant="outline-secondary" className="btn-soft" onClick={onClose} disabled={sending}>
          Cancelar
        </Button>

        {step === STEP.UPLOAD && null}

        {step === STEP.SHEETS && (
          <Button variant="primary" onClick={onContinueSheets} disabled={selectedSheets.size === 0}>
            Continuar
          </Button>
        )}

        {step === STEP.EDIT && (
          <Button variant="primary" onClick={onContinueEdit} disabled={columns.length === 0}>
            Continuar para mapeamento
          </Button>
        )}

        {step === STEP.MAPPING && (
          <Button variant="primary" onClick={onContinueMapping} disabled={comerciaisLoading}>
            Validar e rever contactos
          </Button>
        )}

        {step === STEP.REVIEW && (
          <Button variant="primary" onClick={onContinueReview}>
            Continuar para confirmação
          </Button>
        )}

        {step === STEP.CONFIRM && (
          <Button variant="primary" onClick={onStartImport} disabled={summary.selectedForSend === 0}>
            Iniciar importação
          </Button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// 4) ExportContactsButton — "Exportar contactos" (Exportar todos / filtrados)
// ============================================================================

// Campos exportados — mesmos nomes de campo usados na criação/edição de contactos.
const EXPORT_COLUMNS = [
  { key: "ed_contact_name", label: "Nome" },
  { key: "ed_contact_phone", label: "Telefone" },
  { key: "ed_contact_email", label: "Email" },
  { key: "ed_contact_country", label: "País" },
  { key: "ed_contact_province", label: "Província" },
  { key: "ed_contact_address", label: "Endereço" },
  { key: "ed_contact_type", label: "Tipo" },
  { key: "ed_contact_comercial", label: "Comercial" },
];

// (reutiliza a função asArray() já definida mais acima, junto aos helpers)

function downloadContacts(contacts, filename) {
  const rows = contacts.map((c) => {
    const row = {};
    EXPORT_COLUMNS.forEach((col) => {
      row[col.label] = c[col.key] ?? "";
    });
    return row;
  });
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Contactos");
  XLSX.writeFile(wb, filename);
}

/**
 * Botão de exportação de contactos para Excel.
 *
 * NOTA / ASSUNÇÃO: o endpoint de listagem usado abaixo (`${Hoot()}contacts`)
 * segue o mesmo padrão de `contactsbyid/:id` visto em
 * RegisterOrUpdateContactModal. Se o endpoint real de listagem tiver outro
 * nome, ajuste apenas a constante LIST_ENDPOINT abaixo.
 *
 * @param {object} props
 * @param {object} props.activeFilters - filtros atualmente aplicados na lista
 *   de contactos (ex.: { provincia: 'luanda', tipo: '0' }). Usados apenas na
 *   opção "Exportar filtrados".
 */
const LIST_ENDPOINT = "contacts";

export function ExportContactsButton({ activeFilters = {} }) {
  const [loading, setLoading] = useState(false);

  const handleExport = async (mode) => {
    try {
      setLoading(true);
      const params = mode === "filtrados" ? activeFilters : {};
      const res = await axios.get(`${Hoot()}${LIST_ENDPOINT}`, {
        withCredentials: true,
        params,
      });
      const contacts = asArray(res.data);

      if (contacts.length === 0) {
        toast.info("Não há contactos para exportar.");
        return;
      }

      const filename =
        mode === "filtrados" ? "contactos_filtrados.xlsx" : "contactos_todos.xlsx";
      downloadContacts(contacts, filename);
      toast.success("Exportação concluída!");
    } catch (err) {
      toast.error("Erro ao exportar contactos.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dropdown>
      <Dropdown.Toggle variant="outline-secondary" disabled={loading} style={{ borderRadius: 999 }}>
        {loading ? <Spinner size="sm" animation="border" className="me-2" /> : <i className="bi bi-download me-2" />}
        Exportar contactos
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item onClick={() => handleExport("todos")}>Exportar todos</Dropdown.Item>
        <Dropdown.Item onClick={() => handleExport("filtrados")}>Exportar filtrados</Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}